import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left visual panel */}
      <div className="hidden md:flex md:w-1/2 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-diagonal opacity-30" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full text-white">
          <div className="flex justify-center">
            <img
              src="/vetacar-logo.png"
              alt="VETA CAR"
              className="w-full max-w-[440px] h-auto object-contain block"
              data-testid="login-hero-logo"
            />
          </div>
          <div>
            <p className="veta-tag text-gray-400">Plataforma de Gestión</p>
            <h2 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[0.9] mt-4 max-w-lg">
              Flota.<br />Taller.<br />
              <span className="text-blue-400">Control total.</span>
            </h2>
            <p className="mt-6 text-sm text-gray-300 max-w-md leading-relaxed">
              Conecta empresas Rent-a-Car con talleres y clientes corporativos. Documentación, mantención
              y certificados verificables — todo en un sólo lugar.
            </p>
          </div>
          <div className="flex gap-8">
            <div>
              <p className="font-display text-3xl text-white">100%</p>
              <p className="veta-tag text-gray-400">Multi-tenant</p>
            </div>
            <div>
              <p className="font-display text-3xl text-white">QR</p>
              <p className="veta-tag text-gray-400">Verificación pública</p>
            </div>
            <div>
              <p className="font-display text-3xl text-white">RUT</p>
              <p className="veta-tag text-gray-400">Validación CL</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 flex items-center gap-3">
            <Logo size={40} />
            <span className="font-display text-2xl">VETA CAR</span>
          </div>

          <p className="veta-tag mb-2">Iniciar Sesión</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter leading-none">
            Bienvenido<br />de vuelta.
          </h1>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="veta-tag block mb-2">Correo electrónico</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.cl"
                className="w-full h-touch px-4 border border-gray-300 focus:outline-none focus:border-black bg-white font-mono text-sm"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="veta-tag block mb-2">Contraseña</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-touch px-4 border border-gray-300 focus:outline-none focus:border-black bg-white font-mono text-sm"
                data-testid="login-password"
              />
            </div>

            {error && (
              <div className="status-danger px-4 py-3 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-touch flex items-center justify-center gap-3 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 transition-colors active:scale-[0.98] disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? "Ingresando..." : "Ingresar"} <ArrowRight size={16} strokeWidth={2.5} />
            </button>

            <p className="text-sm text-gray-600 text-center pt-4">
              ¿No tienes cuenta?{" "}
              <Link to="/register" className="text-blue-600 font-bold underline" data-testid="link-register">
                Regístrate aquí
              </Link>
            </p>
            <div className="border border-gray-200 p-3 text-xs font-mono text-gray-600">
              <p className="font-bold text-black mb-1">Demo SuperAdmin:</p>
              admin@vetacar.cl / admin123
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
